<template>
  <div
    v-if="!sceneStore.loading"
    class="dragging flex min-h-dvh flex-col overflow-hidden md:min-h-[91dvh] xl:min-h-[97dvh]">
    <PanelForm class="-my-2 flex flex-1 overflow-hidden">
      <div
        v-if="filteredObjects.length === 0"
        class="mx-auto flex h-full w-full items-center justify-center pt-2 text-center text-gray-400"
        :class="[collapsed ? 'text-xs' : '']">
        {{ searchString.length > 0 ? `No  object with "${searchString}" found` : 'No objects in the scene' }}
      </div>
      <div v-else class="flex w-full" :class="{ 'pointer-events-none blur-lg': appStore.treeObjectCloningInProgress }">
        <div
          ref="leftPanel"
          :style="{ minWidth: leftPanelWidth }"
          class="max-h-screen overflow-y-auto py-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div class="mx-2 pb-2 transition-all delay-300 ease-in-out" v-if="showSearchField">
            <DInputText v-model="searchString" fluid placeholder="Search..." />
          </div>
          <SelectionItem
            v-for="object in filteredObjects"
            :key="object.code"
            :item="object"
            @contextmenu="showObjectContext" />
          <div
            v-if="appStore.treeParentDragOver"
            @drop="handleDrop"
            @dragover="handleDragOver"
            class="border-primary bg-primary/20 mx-2 mt-2 flex items-center justify-center gap-2 rounded border border-dashed py-2 font-semibold text-gray-500 transition-all delay-100 ease-in-out hover:border-gray-500 hover:text-gray-400">
            <i class="pi pi-arrow-circle-down text-lg" />
            <span class="text-xs">Drag here</span>
          </div>
        </div>

        <div v-if="appStore.treeParameterPanelOpen" class="relative flex" :style="{ minWidth: rightPanelWidth }">
          <div ref="rightPanel" class="relative flex" :style="{ width: rightPanelWidth }">
            <div
              class="group absolute z-50 flex h-full w-0.5 cursor-ew-resize items-center justify-center bg-gray-700 transition-colors hover:w-1 hover:bg-gray-600"
              @mousedown="startResize"></div>

            <SettingPanelControls
              dataType="selectedNode"
              class="max-h-screen overflow-y-auto bg-gray-800 px-4 py-2 text-gray-200 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              v-if="selectionStore.selectedObjectId && !selectionStore.selectedMeshId" />

            <MeshSettingPanel v-if="selectionStore.selectedMeshId" />
          </div>
        </div>
      </div>
    </PanelForm>

    <DContextMenu2 ref="objectMenuRef" :model="objectItems" />
  </div>
</template>

<script setup>
import { PanelForm } from '@/components';
import { useNodeManager } from '@/composables/useNodeManager';
import { PANEL_WIDTH } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSceneStore } from '@/stores/api/scene';
import { useAppStore } from '@/stores/appStore';
import { useSelectionStore } from '@/stores/ui/selection';
import { DContextMenu2, DInputText, useConfirm } from '@disguise/cloud-ui';
import { computed, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import MeshSettingPanel from '../MeshSettingPanel.vue';
import SettingPanelControls from '../SettingPanelControls.vue';
import SelectionItem from './SelectionItem.vue';

const RIGHT_MIN_WIDTH = 200;
const LEFT_MIN_WIDTH = 50;

const { confirmDialog } = useConfirm();
const { removeNode, duplicateNode, moveNode } = useNodeManager();
const sceneStore = useSceneStore();
const cruxModuleStore = useCruxModuleStore();
const selectionStore = useSelectionStore();
const appStore = useAppStore();

const leftPanelWidth = ref(null);
const rightPanelWidth = ref(null);
const ifAlreadyPanelResized = ref(false);
const showSearchField = ref(true);

const leftPanelRef = useTemplateRef('leftPanel');

const props = defineProps({
  selectedItem: {
    type: Object,
  },
  controlsRef: {
    type: Object,
  },
  sceneNodeRefs: {
    type: Object,
  },
});

const searchString = ref('');
const filteredObjects = computed(() =>
  cruxModuleStore
    .getRootSceneTreeModules()
    .filter((m) => !m.data.parentCode && m.name.toLowerCase().includes(searchString.value.toLowerCase()))
);

const collapsed = computed(() => appStore.rightSettingPanelWidth < PANEL_WIDTH.RIGHT_MAX);

const currentObjectId = ref(null);
const objectMenuRef = ref();
const objectItems = ref([
  {
    label: 'Clone',
    icon: 'pi pi-copy',
    command: () => {
      duplicateNode(currentObjectId.value);
    },
  },
  {
    label: 'Delete',
    icon: 'pi pi-trash',
    command: () => {
      confirmDialog('Confirmation', 'Are you sure you want to delete this object?', 'Delete', 'Cancel')
        .then((response) => {
          if (response) removeNode(currentObjectId.value);
        })
        .catch((err) => {});
    },
  },
]);

const showObjectContext = ($event, id) => {
  currentObjectId.value = id;
  objectMenuRef.value.show($event);
};

const startResize = () => {
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.addEventListener('mouseleave', stopResize);
};

const handleResize = (e) => {
  if (e.movementX === 0) return;
  if (!leftPanelRef.value?.parentElement) return;

  const container = leftPanelRef.value.parentElement.getBoundingClientRect();
  const newLeftWidth = e.clientX - container.left;
  const newRightWidth = container.width - newLeftWidth;

  if (newLeftWidth < LEFT_MIN_WIDTH || newRightWidth < RIGHT_MIN_WIDTH) return;

  ifAlreadyPanelResized.value = true;
  leftPanelWidth.value = newLeftWidth + 'px';
  rightPanelWidth.value = newRightWidth + 'px';
  appStore.setTreeDisplayOnlyIcon(newLeftWidth < 80);
  showSearchField.value = newLeftWidth >= 100;
};

const stopResize = () => {
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mouseup', stopResize);
  document.removeEventListener('mouseleave', stopResize);
};

const adjustPanelsOnOuterResize = () => {
  const container = leftPanelRef.value?.parentElement?.getBoundingClientRect();
  if (!container?.width) return;
  const rawRightWidth = appStore.treeParameterPanelOpen && rightPanelWidth.value ? parseInt(rightPanelWidth.value) : 0;
  const clampedRightWidth = Math.min(rawRightWidth, container.width - LEFT_MIN_WIDTH);
  const newLeftWidth = container.width - clampedRightWidth;
  if (newLeftWidth >= LEFT_MIN_WIDTH) {
    if (appStore.treeParameterPanelOpen) {
      rightPanelWidth.value = Math.max(clampedRightWidth, RIGHT_MIN_WIDTH) + 'px';
    }
    leftPanelWidth.value = newLeftWidth + 'px';
    appStore.setTreeDisplayOnlyIcon(newLeftWidth < 80);
    showSearchField.value = newLeftWidth >= 100;
  }
};

const resetPanelSizes = () => {
  const containerWidth = leftPanelRef.value?.parentElement.getBoundingClientRect()?.width;
  appStore.setTreeDisplayOnlyIcon(false);
  leftPanelWidth.value = containerWidth + 'px';
};

const splitSizeBetweenPanels = () => {
  const container = leftPanelRef.value?.parentElement?.getBoundingClientRect();
  if (!container?.width) return;

  const containerWidth = container.width;
  const halfWidth = containerWidth / 2;

  if (ifAlreadyPanelResized.value) {
    const rightWidth = rightPanelWidth.value ? parseInt(rightPanelWidth.value) : halfWidth;
    const newLeftWidth = containerWidth - rightWidth;
    leftPanelWidth.value = newLeftWidth + 'px';
    appStore.setTreeDisplayOnlyIcon(newLeftWidth < 80);
    showSearchField.value = newLeftWidth >= 100;
    return;
  }

  if (appStore.treeParameterPanelOpen && selectionStore.selectedObjectId) {
    leftPanelWidth.value = halfWidth + 'px';
    rightPanelWidth.value = halfWidth + 'px';
    appStore.setTreeDisplayOnlyIcon(halfWidth < 80);
    showSearchField.value = halfWidth >= 100;
  } else {
    resetPanelSizes();
  }
};

watch(() => appStore.rightSettingPanelWidth, adjustPanelsOnOuterResize);

watch(
  () => appStore.treeParameterPanelOpen,
  (isOpen) => {
    if (isOpen && selectionStore.selectedObjectId) splitSizeBetweenPanels();
    else {
      ifAlreadyPanelResized.value = false;
      resetPanelSizes();
    }
  }
);

watch(
  () => selectionStore.selectedObjectId,
  (newVal) => {
    if (newVal && appStore.treeParameterPanelOpen) splitSizeBetweenPanels();
    else {
      ifAlreadyPanelResized.value = false;
      resetPanelSizes();
    }
  }
);

onMounted(() => {
  splitSizeBetweenPanels();
});

onUnmounted(() => {
  leftPanelWidth.value = '100%';
  rightPanelWidth.value = '100%';
  ifAlreadyPanelResized.value = false;
  appStore.setTreeDisplayOnlyIcon(false);
});

const handleDragOver = (e) => {
  e.preventDefault();
};

const handleDrop = async (e) => {
  e.preventDefault();
  const code = e.dataTransfer.getData('text/plain');
  try {
    await moveNode(code, null);
  } finally {
    appStore.setTreeParentDragOver(false);
  }
};
</script>
